import { useEffect, useState } from "react";
import { ChevronLeft, X } from "lucide-react";
import { Button, Screen, Spinner, Text } from "@ferrivia/ui";
import { useQuestionnaireCopy, SYMPTOM_STEP_ID } from "@ferrivia/questionnaire-ui";
import { useTranslation } from "@ferrivia/i18n";
import { FullSymptomCheck } from "../components/SymptomCheckin";
import s from "./SymptomStepScreen.module.css";

export interface SymptomStepScreenProps {
  ensureStarted(): Promise<void>;
  onSaved(): void;
  onBack(): void;
  onClose(): void;
}

/** The questionnaire's required symptom step: the full symptom check, saved as
    an ordinary check-in. The form waits for an open submission so the saved
    check-in postdates its `started_at`, which is what counts it as done. */
export function SymptomStepScreen({ ensureStarted, onSaved, onBack, onClose }: SymptomStepScreenProps) {
  const { t } = useTranslation("client");
  const copy = useQuestionnaireCopy();
  const [startState, setStartState] = useState<"starting" | "ready" | "failed">("starting");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setStartState("starting");
    ensureStarted().then(
      () => {
        if (!cancelled) setStartState("ready");
      },
      () => {
        if (!cancelled) setStartState("failed");
      },
    );
    return () => {
      cancelled = true;
    };
  }, [ensureStarted, attempt]);

  return (
    <Screen className={s.root}>
      <div className={s.header}>
        <button type="button" className={s.back} onClick={onBack}>
          <ChevronLeft aria-hidden size={18} />
          {t("questionnaire.back", "Takaisin")}
        </button>
        <button type="button" className={s.close} onClick={onClose} aria-label={t("questionnaire.close", "Sulje")}>
          <X aria-hidden size={20} />
        </button>
      </div>

      <div className={s.scroll}>
        <Text as="p" size="xs" weight="medium" className={s.eyebrow}>
          {copy.sectionFor(SYMPTOM_STEP_ID)?.title ?? SYMPTOM_STEP_ID}
        </Text>
        {startState === "ready" ? <FullSymptomCheck onSaved={onSaved} /> : null}
        {startState === "starting" ? (
          <div className={s.centered}>
            <Spinner />
          </div>
        ) : null}
        {startState === "failed" ? (
          <div className={s.centered}>
            <Text as="p" size="md" className={s.errorText} role="alert">
              {t("questionnaire.symptomStep.startFailed", "Oirekyselyä ei voitu avata.")}
            </Text>
            <Button onClick={() => setAttempt((n) => n + 1)}>{t("questionnaire.retryLoad", "Yritä uudelleen")}</Button>
          </div>
        ) : null}
      </div>
    </Screen>
  );
}
